

import React, { JSX, useEffect } from "react";
import "ol/ol.css";
import { fromLonLat } from "ol/proj";
import { Overlay } from "ol";
import "./CustomMarkerImage.scss";
import { useMap } from "./Context";
import "./index.scss";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "@store/store";
import { setAmenityROWField } from "@modules/stepper-row/redux/amenitesROWSlice";
import { setLegacyField } from "@modules/legacy-application-update/redux/slice/legacySlice";
import { setCoordinateSite } from "@modules/stepper-row/redux/stepperROWSlice";
import type { RootReducerType } from "@store/store";

interface LocationMarkerProps {
  coordinates?: [number, number] | [any, any],
  html: string | null
}

const LocationMarker: React.FC<LocationMarkerProps> = ({ coordinates, html }) => {

  const { map } = useMap();
  const dispatch = useDispatch<AppDispatch>();
  const { start_latitude } = useSelector((state: RootReducerType) => state.amenitiesROW);

  useEffect(() => {
    // Check if map is loaded
    if (!map) return;

    //Create the marker element (HTML)
    const markerElement = document.createElement("div");
    markerElement.className = `custom-marker`;
    markerElement.innerHTML = html?.toString() || '';

    // Function to resize the element based on zoom level
    const resizeMarker = () => {
      const zoomLevel = map.getView().getZoom();
      if (zoomLevel !== undefined) {
        const locationMarkerElem = markerElement.querySelector('div.location-marker');
        if (locationMarkerElem instanceof HTMLElement) {
          locationMarkerElem.style.minWidth = `12rem`;
        }
        if (Math.round(zoomLevel) > 10) {
          if (locationMarkerElem instanceof HTMLElement) {
            locationMarkerElem.style.minWidth = `18rem`;
          }
        } else if (Math.round(zoomLevel) < 10 && Math.round(zoomLevel) > 7) {
          if (locationMarkerElem instanceof HTMLElement) {
            locationMarkerElem.style.minWidth = `${zoomLevel * 1.4}rem`;
          }
        }
      }
    };

    map.on('moveend', () => {
      resizeMarker();
    });

    const resetMarker = (marker: string) => {
      dispatch(
        setAmenityROWField({
          name: `${marker}_latitude`,
          value: "",
        })
      );
      dispatch(
        setAmenityROWField({
          name: `${marker}_longitude`,
          value: "",
        })
      );
      dispatch(
        setAmenityROWField({
          name: `${marker}_chainage_km`,
          value: 0,
        })
      );
      dispatch(
        setAmenityROWField({
          name: `${marker}_chainage_m`,
          value: 0,
        })
      );


      dispatch(
        setLegacyField({
          name: `${marker}_latitude`,
          value: "",
        })
      );
      dispatch(
        setLegacyField({
          name: `${marker}_longitude`,
          value: "",
        })
      );
      dispatch(
        setLegacyField({
          name: `${marker}_chainage_km`,
          value: 0,
        })
      );
      dispatch(
        setLegacyField({
          name: `${marker}_chainage_m`,
          value: 0,
        })
      );

      if (marker === "start") {
        dispatch(setCoordinateSite(marker));
        
      } else {
        if (marker === "end" && (start_latitude.toString().length === 0 || start_latitude === null)) {
          dispatch(setCoordinateSite("start"));
        } else {
          dispatch(setCoordinateSite(marker));
        }
      }
    }

    const position = coordinates ? fromLonLat(coordinates) : undefined;
    // Create an Overlay and attach the marker element
    const overlay = new Overlay({
      element: coordinates?.[0] === '0' || coordinates?.[1] === '0' ? undefined : markerElement,
      position: position,
      positioning: "center-center",
      offset: [-20, -100],
    });

    // Add overlay to the map
    map.removeOverlay(overlay);
    map.addOverlay(overlay);

    markerElement.addEventListener('click', (event: any) => {
      if (event.target.className === 'close-icon') {
        map.removeOverlay(overlay);
      }
      if (event.target.id === 'start-pin') {

        resetMarker("start");
      }
      if (event.target.id === 'end-pin') {
        resetMarker("end");
      }
    });

    return () => {
      map.removeOverlay(overlay);
    };

  }, [map, coordinates, html]);

  return null;
};

export default LocationMarker;